import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import { getFormattedDate } from "../../../utils/commonFunctions";
import {
  pointsDetailsSubtitleStyle,
  pointsDetailsTitleStyle
} from "../../../components/styles/commonStyles";

interface UserPointsDetailsPageProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  businessId: string;
  establishmentName?: string;
}

interface PointsDetailsData {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  Points: number;
  DateCreated: string;
  SourceName: string;
}

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

const UserPointsDetailsPage = ({
  open,
  onClose,
  userId,
  businessId,
  establishmentName = ""
}: UserPointsDetailsPageProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [points, setPoints] = useState<PointsDetailsData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchPoints = useCallback(() => {
    if (!userId || !businessId) return;
    setLoading(true);
    setError(null);
    const apiPageNumber = currentPage + 1;
    const url = `${apiEndpoint}/loyaltyprogram/points/user/${userId}/business/${businessId}?PageNumber=${apiPageNumber}&PageSize=${pageSize}`;
    fetch(url, {
      headers: {
        Authorization: `bearer ${localStorage.getItem("accessToken")}`,
        "Content-Type": "application/json"
      }
    })
      .then((res) => res.json())
      .then((data) => {
        const mappedData = (data.Data || data.data || []).map((item: any) => ({
          PatronId: item.PatronId,
          MerchantId: item.MerchantId,
          EstablishmentName: item.EstablishmentName,
          Points: item.Points,
          DateCreated: item.DateCreated,
          SourceName: item.SourceName
        }));
        setPoints(mappedData);
        const total = data.TotalCount || data.totalCount || data.total || 0;
        setTotalCount(total);
      })
      .catch(() => setError("Failed to fetch points details"))
      .finally(() => setLoading(false));
  }, [userId, businessId, currentPage, pageSize]);

  useEffect(() => {
    if (open) {
      fetchPoints();
    }
  }, [open, fetchPoints]);

  const handleClose = () => {
    setCurrentPage(0);
    setPageSize(10);
    setError(null);
    onClose();
  };

  const handleRefresh = () => {
    fetchPoints();
  };

  const handleChangePage = (event: any, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const hasMorePages = (currentPage + 1) * pageSize < totalCount;

  const columns = [
    {
      id: "PatronId",
      label: "Patron ID",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "MerchantId",
      label: "Merchant ID",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Points",
      label: "Points",
      minWidth: 100,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "DateCreated",
      label: "Created At",
      minWidth: 180,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "SourceName",
      label: "Source Name",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="h5" component="div" sx={pointsDetailsTitleStyle}>
            Point Details
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={pointsDetailsSubtitleStyle}
          >
            {establishmentName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            size="small"
            title="Refresh"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <CustomTableWithPagination
          columns={columns}
          data={points}
          apiStatus={loading ? "fetching" : "idle"}
          payloadSize={pageSize}
          currentPage={currentPage}
          totalCount={totalCount}
          lastEvaluatedKey={hasMorePages ? "hasMore" : null}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          isShowPagination={true}
          noDataFoundMessage="No points details found."
        />
      </DialogContent>
    </Dialog>
  );
};

export default UserPointsDetailsPage;
